import { DESIGN_OPTIONS } from 'constants/energyAdvisor';
import { edet } from 'constants/mapCoordinates';
import { UTILITIES } from 'constants/utilitiesMappers';
import { PAYMENT_GATEWAYS } from 'constants/payments';
import { ROUTES } from 'constants/routes';

import Colors from './colors/edet';
import SassColors from './edet/scss/_colors.scss';

const EMAIL = 'email';

const utilitySettings = {
  i18n: 'Edet',
  colors: Colors,
  sassColors: SassColors,
  assets: 'edet',
  appTitle: 'EDET',
  utilityId: 6,
  mappers: UTILITIES.edet,
  includesHolderChangeProcedure: false,
  canChangePrimaryEmail: false,
  includesTerminationService: false,
  includesRegisterAccount: false,
  usePayments: false,
  usePaymentDocuments: true,
  usePoints: false,
  deleteAccount: true,
  shouldUseAccOptimization: false,
  ACCOUNT_LENGTH: 9,
  clientNumberRegex: /^[1-9]{1}[0-9]{8}$/,
  lineSections: true,
  showUserAccountRelationship: false,
  useClientSummary: false,
  useChatbot: false,
  canUseDashboardWithoutAccounts: false,
  fontFamily: 'Helvetica',
  mobileSidebarLogo: false,
  mobileTopbarLogo: false,
  disabledMobileAvatarEnergyAdvisor: true,
  UTModal: {
    modalBorderRadius: 0
  },
  turns: {
    active: false
  },
  // Service Adherence
  automaticDebitShortcuts: true,
  digitalBillShortcuts: true,
  userDigitalBillAdherence: true,
  digitalBillWithForm: false,
  // Sign in configuration
  signInMethod: EMAIL,
  passwordRequireNumberAndLetter: false,
  passwordMinLength: 6,
  passwordMaxLength: 20,
  PASSWORD_CHANGE_EXTERNAL_TOKEN: 'edelap_token', // TODO: Try to change this 'edelap_token' with something more generic ant put in signin constants
  hideAppTitle: true,
  authAvatarSquared: true,
  authAvatarWithShadow: false,
  authAvatarWithBackground: false,
  authBackgroundColorHorizontal: true,
  authImageBackground: false,
  authLogoInForm: true,
  authFormCentered: true,
  buttonRadius: 0,
  squaredToggle: true,
  payExternalAcc: false,
  errorScreenWithImage: false,
  analytics: {
    trackDigitalBillOnRegister: false
  },
  withTooltip: false,
  withAppDownloader: false,
  accountSelectKeys: [
    { prefix: '', key: 'client_number' },
    { prefix: '-', key: 'address' },
    { prefix: '-', key: 'holder_name' }
  ],
  appleStoreURL: 'https://itunes.apple.com/ar/app/edes-m%C3%B3vil/id1376929616?mt=8',
  googlePlayStoreURL: 'https://play.google.com/store/apps/details?id=com.utility.edes&hl=en_US',
  googleMap: {
    defaultCoordinates: {
      latitude: -38.718776,
      longitude: -62.26912
    },
    colors: {
      polygon: Colors.transparent,
      borderPolygon: Colors.primary
    },
    zoom: {
      initial: 15,
      addressValid: 15,
      addressInvalid: 10
    },
    withPolygon: true,
    withGeolocation: true,
    path: edet
  },
  signIn: {
    conditionalRequestGender: false,
    minDocNumber: 10000,
    maxDocNumber: 10000000,
    iconField: true
  },
  // Sign up flow
  signUpFlow: {
    PersonalInformation: true,
    IdentityValidation: false,
    UserInformation: true,
    TermsAndConditions: false,
    EmailValidation: true,
    AddAccount: true,
    AccountRelationshipValidation: true,
    ServiceAdherence: true,
    RelatedAccounts: true
  },
  signUp: {
    retrieveUserIdentification: false,
    personalInformationFields: {
      platform_discovery: { show: false, required: false },
      document_type: { show: false, required: true },
      document_number: { show: false, required: true },
      gender: { show: false, required: true },
      name: { show: true, required: true },
      last_name: { show: true, required: true },
      bornDate: { show: true, required: true },
      carrier: { show: false, requiredOn: 'cellNumber' },
      cellNumber: { show: true, required: true, dependsOf: null },
      phone: { show: true, required: false, dependsOf: null },
      contact_first_name: { show: false, required: true },
      contact_last_name: { show: false, required: true },
      captcha: { show: true, required: true }
    },
    personalInformationFullWidthFields: false,
    nameMaxLength: 20,
    lastNameMaxLength: 20
  },

  // Dashboard configuration
  showAmountChart: false,
  showAmountComparison: false,
  showAnnualProgress: false,
  topbarUtilityLogo: true,
  topbarCustomIcons: true,
  topbar: {
    hideLoginButton: true,
    logoExternalLink: 'https://www.edetsa.com/info/pwa'
  },
  utilityLinks: [],
  addSupplies: true,
  simulatorLink: 'https://simulador.infoedes.com/',
  simulatorButton: true,
  accountStatus: {
    consumptionHistory: true,
    consumptionHistoryPeriods: 12,
    digitalBillButtonWithTitle: false
  },

  // Bills configuration
  billsPageSize: 12,
  disableBillsPagination: true,
  PaymentDocumentSelection: false,
  encodedDocumentDownload: true,
  useBillsBalance: false,
  useBillsSurchargeAmount: true,
  multipleBillsDownload: false,
  claims: {
    useClaims: true,
    useClaimList: true,
    openableRow: false,
    openClaimsFromSidebar: false,
    showAccountSelector: true,
    cancelClaimComment: false,
    reiterationClaimComment: false,
    showClaimFormsOnSidebar: false
  },
  procedures: {
    useProcedures: true,
    useProcedureList: false,
    showAccountSelector: false
  },
  bills: {
    billDownloadOnPDF: false,
    lastBill: false,
    downloadIcon: true,
    downloadIconBillsScreen: true,
    downloadIconMobile: false,
    showAccountSelector: true
  },
  billsColumns: {
    period: { width: 0.07, order: 1 },
    issued_on: { width: 0.09, order: 2 },
    document_type: { width: 0.18, order: 3 },
    document_number: { width: 0.13, order: 4 },
    first_expiration_on: { width: 0.11, order: 5 },
    status: { width: 0.14, order: 6 },
    amount: { width: 0.11, order: 7 },
    balance: { width: 0.08, order: 8 },
    download: { width: 0.09, order: 9 },
    payable: { width: 0.09, order: 10 }
  },
  maxAmountThreshold: 999999999,
  maxDocsThreshold: 999999999,

  // Public payment configuration
  userDataRequired: true, // email or doc not required for coupon
  allowPaymentDocumentSelection: false, // if true, it goes to payment screen after pressing coupon button
  allowMultipleCoupons: false,
  showPaymentPlaces: false,

  // Settings view configuration
  userDataManagement: {
    landlineRequired: false,
    cellPhoneRequired: true,
    phoneRequired: false,
    defaultPhoneRequired: false,
    requirePasswordForUserDataManagement: false,
    useCarrier: false,
    useDefaultPhone: false,
    shouldUpdateEmail: false,
    mayDeletePhones: false,
    MAX_PHONES: 1,
    usePhoneFields: true,
    emailMaxLength: 255,
    rowDescIcons: false,
    addPhoneButton: true,
    userConfigBlueMargin: true
  },

  dashboard: {
    pendingFineAmountQuery: false,
    pendingWarrantyDepositQuery: false
  },

  publicDashboard: {
    publicPayment: false,
    lastBillQuery: false,
    balanceQuery: false,
    procedures: true,
    claims: false,
    complaints: false,
    pendingFineAmountQuery: false,
    pendingWarrantyDepositQuery: false,
    socialRate: false,
    home: true,
    changeDueDate: false,
    usePublicBanner: false,
    simulator: false
  },
  publicHomeWithRegister: false,
  publicHomeWithLogo: true,
  publicHomePage: [
    {
      content: 'Bienvenido a la Sucursal Virtual de EDET'
    },
    {
      content: 'Ingresa a generar trámite para enviar tu solicitud'
    }
  ],

  homeButtonWithClientNumber: true,

  clientSummary: {
    selectAllAccounts: false,
    multipleAccountsPayment: false,
    useStatusColumn: false
  },

  forms: {
    showFormCategoriesListTitle: false
  },

  socialRate: {
    useSocialRate: true,
    content: [
      {
        title: 'Criterios para la determinación de la tarifa social',
        subtitle:
          'El Ministerio de Energía y Minería de la Nación ha determinado los siguientes criterios de elegibilidad, los cuales serán aplicados por el Consejo Nacional de Coordinación de Políticas Sociales de la Presidencia de la Nación definiendo los beneficiarios, a través de la red SINTyS (Sistema de Identificación Nacional Tributario y Social):',
        items: [
          {
            content:
              'Ser jubilado o pensionado por un monto equivalente a dos veces el haber mínimo nacional',
            id: 1
          },
          {
            content:
              'Personas con empleo en relación de dependencia, que perciben una remuneración bruta menor o igual a 2 Salarios Mínimos Vital y Móvil (SMVM)',
            id: 2
          },
          { content: 'Ser titular de programas sociales', id: 3 },
          { content: 'Estar inscripto en el Régimen de Monotributo Social', id: 4 },
          {
            content:
              'Estar incorporado en el Régimen Especial de Seguridad Social para empleados del Servicio Doméstico (artículo 21 de la Ley N° 25.239)',
            id: 5
          },
          { content: 'Estar percibiendo el seguro de desempleo', id: 6 },
          { content: 'Contar con certificado de discapacidad', id: 7 }
        ],
        withBullet: true
      },
      {
        title: 'Criterios de exclusión del beneficio',
        items: [
          { content: 'Cruce por padrón de fallecidos', id: 1 },
          {
            content: 'Registro de propiedad Inmueble —quedará excluido cuando sea titular de más de uno',
            id: 2
          },
          {
            content:
              'Padrón de Automotores —quedarán excluidos aquellos cuyos modelos tengan hasta 10 años de antigüedad',
            id: 3
          },
          {
            content:
              'Embarcaciones de lujo —quedarán excluidos quienes posean aeronaves o embarcaciones de lujo',
            id: 4
          }
        ],
        withBullet: true
      }
    ],
    showClientNumber: false,
    showAddress: false,
    manualInput: false,
    showCancelButton: false
  },
  complaints: {
    includeInTechnicalService: true,
    useComplaints: true,
    complaintsForm: true,
    withCrimeAlert: true,
    withSubtitles: true,
    withBullets: true,
    withLine: true,
    squaredContactItems: true,
    bullets: [
      { content: 'Crea peligros mortales de incendio y seguridad.', id: 1 },
      {
        content:
          'El robo puede resultar en lesiones o muerte para aquellos que roban la energía, sus familias, vecinos y empleados de servicios públicos.',
        id: 2
      },
      { content: 'El robo de energía también puede causar daños materiales costosos.', id: 3 },
      {
        content:
          'Las pérdidas de la energía robada aumentan los gastos de operación de la Utility y el costo de la energía para nuestros clientes.',
        id: 4
      }
    ],
    contactPhone: '0800-999-3337',
    contactMail: 'cc.edes@edessa.com.ar'
  },
  pendingWarrantyDepositQuery: {
    content: ''
  },

  pendingFineAmountQuery: {
    content: ''
  },

  energyAdvisor: {
    design: DESIGN_OPTIONS[0],
    tipsWithTitle: false
  },

  changeDueDate: {
    active: false
  },

  paymentFlow: {
    useCardSelectionStep: false,
    provider: PAYMENT_GATEWAYS.FIRST_DATA
  },

  survey: {
    payments: false,
    procedures: false,
    claim: false
  },

  payments: {
    paymentForNis: true,
    paymentsWithEmail: false,
    paymentDocumentDownloadOnPDF: false,
    active: true,
    publicPayment: false,
    publicPaymentByNis: false,
    multipleDownloadRow: false,
    paymentDocumentModal: true,
    columns: {
      checkbox: { show: true, width: 0.04, order: 1, showOnMobile: true },
      period: { show: true, width: 0.12, order: 2, showOnMobile: true },
      issued_on: { show: false, width: 0.09, order: 2, showOnMobile: false },
      document_type: { show: false, width: 0.18, order: 3, showOnMobile: false },
      document_number: { show: false, width: 0.13, order: 4, showOnMobile: false },
      first_expiration_on: { show: false, width: 0.11, order: 5, showOnMobile: false },
      status: { show: false, width: 0.14, order: 6, showOnMobile: false },
      amount: { show: false, width: 0.11, order: 7, showOnMobile: false },
      balance: { show: true, width: 0.12, order: 3, showOnMobile: true },
      download: { show: false, width: 0.12, order: 9, showOnMobile: false },
      barcode: { show: true, width: 0.2, order: 4, showOnMobile: true },
      external_id: { show: true, width: 0.13, order: 5, showOnMobile: true }
    }
  },

  balanceAndLastBillQuery: {
    showCancelButton: false
  },

  accountAlerts: {
    active: true,
    claimFormId: 72
  },

  alerts: {
    withoutIcon: false
  },

  accountPaymentDocsForm: {
    showCancelButton: false
  },

  imageResizing: {
    maxWidth: 1000,
    maxHeight: 1000,
    imageType: 'image/png'
  },
  DEFAULT_ROUTES: {
    RETRIEVE_SESSION: ROUTES.PUBLIC_HOME,
    PRIVATE: ROUTES.PUBLIC_HOME,
    RESTRICTED: ROUTES.PUBLIC_HOME
  },
  RESTRICTED_ROUTES: [ROUTES.PUBLIC_DASHBOARD, ROUTES.PUBLIC_HOME]
};

export default utilitySettings;
